/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-visit-schedules.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';

import ScheduleVersionsGrid from '@components/admin/schedule-versions-grid';
import ScheduleBar from '@components/admin/schedule-versions-grid/schedule-bar';
import {
    ISVTGroupedVersions,
    SVTScheduleVersionFields,
} from '@components/admin/schedule-versions-grid/schedule-versions-grid.interface';
import ImportAndTRSetupDialog from '@components/admin/studies/import-and-tr-setup-dialog';
import { AdminStudyVisitSchedulesProps } from '@pages/admin/study/admin-study-visit-schedules/admin-study-visit-schedule.interface';
import DeleteScheduleVersionDialog from '@pages/admin/study/delete-schedule-version';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useAuthorization, useBoolean, useTranslation } from 'hooks';
import { GET_STUDY_COHORTS } from 'queries/study-cohorts';
import { GET_STUDY_TREATMENT_ARMS } from 'queries/study-treatment-arms';
import { GET_STUDY_VISIT_SCHEDULES } from 'queries/study-visit-schedules';
import NotificationService from 'utils/notification';
import AddVisitSchedule from 'views/components/admin/add-visit-schedule';
import ViewSubjectVisitTemplateDialog from 'views/components/admin/view-subject-visit-template';
import Loading from 'views/components/core/loading';
import Error from 'views/components/error/error';
import Constants from '@utils/constants';
import { getLabels } from '@utils/convertor';
import { notEmpty } from '@utils/utility-helpers';
import { StudyTreatmentArmFields, StudyCohortFields, StudyFields } from '@queries/generated/fragments.generated';
import { getStudy, getStudyVariables } from '@queries/generated/studies.generated';
import { studyCohorts, studyCohortsVariables } from '@queries/generated/study-cohorts.generated';
import { treatmentArms, treatmentArmsVariables } from '@queries/generated/study-treatment-arms.generated';
import {
    studyVisitSchedules,
    studyVisitSchedules_OpraQueries_studyVisitSchedules_StudySubjectVisitScheduleTemplate as studyVisitSchedules_studyVisitSchedules,
    studyVisitSchedulesVariables,
} from '@queries/generated/study-visit-schedules.generated';
import { GET_STUDY } from '@queries/studies';

import './styles.scss';

const AdminStudyVisitSchedules = ({ studyId, canEdit, isSysAdmin }: AdminStudyVisitSchedulesProps) => {
    const user = useAuthorization();
    const { t } = useTranslation();

    const { loading, error, data } = useQuery<studyVisitSchedules, studyVisitSchedulesVariables>(
        GET_STUDY_VISIT_SCHEDULES,
        { variables: { studyId } },
    );
    const {
        loading: loadingTreatmentArms,
        error: errorTreatmentArms,
        data: dataTreatmentArms,
    } = useQuery<treatmentArms, treatmentArmsVariables>(GET_STUDY_TREATMENT_ARMS, { variables: { studyId } });
    const {
        loading: loadingCohorts,
        error: errorCohorts,
        data: dataCohorts,
    } = useQuery<studyCohorts, studyCohortsVariables>(GET_STUDY_COHORTS, { variables: { studyId } });

    const [loadStudy, { data: getStudyData, loading: getStudyLoading }] = useLazyQuery<getStudy, getStudyVariables>(
        GET_STUDY,
    );
    const [schedules, setSchedules] = useState<SVTScheduleVersionFields[]>([]);
    const [groupedVersions, setGroupedVersions] = useState<ISVTGroupedVersions>({});
    const [currentSchedule, setCurrentSchedule] = useState<SVTScheduleVersionFields | null | undefined>(null);
    const [activeCohorts, setActiveCohorts] = useState<StudyCohortFields[]>([]);
    const [activeTreatmentArms, setActiveTreatmentArms] = useState<StudyTreatmentArmFields[]>([]);
    const [scheduleDialogType, setScheduleDialogType] = useState<string | null>(null);
    const [hasMultipleVersions, setHasMultipleVersions] = useState<boolean>(false);
    const [latestSchedule, setLatestSchedule] = useState<SVTScheduleVersionFields | null | undefined>(null);

    const openAddSchedules = useBoolean(false);
    const openDeleteVersions = useBoolean(false);
    const openImportAndTRSetupDialog = useBoolean(false);
    const showViewSVTDialog = useBoolean(false);
    const [study, setStudy] = useState<StudyFields | null>(null);

    useEffect(() => {
        loadStudy({
            variables: {
                id: studyId,
            },
        });
    }, [studyId]);

    const groupScheduleVersions = (items: SVTScheduleVersionFields[]) => {
        const groupedItems: ISVTGroupedVersions = {};
        items.forEach((item) => {
            if (groupedItems[item.groupId ?? 0]) {
                groupedItems[item.groupId ?? 0].push(item);
            } else {
                groupedItems[item.groupId ?? 0] = [item];
            }
        });

        return groupedItems;
    };

    const restructureSchedules = (items: (studyVisitSchedules_studyVisitSchedules | null)[]) => {
        if (items.length) {
            let newSchedules: SVTScheduleVersionFields[] = items
                .filter(notEmpty)
                .map((item: studyVisitSchedules_studyVisitSchedules) => ({
                    ...item,
                    description: item.description || '',
                    _treatmentArms: getLabels(item.treatmentArmIds, activeTreatmentArms, 'name'),
                    _cohorts: getLabels(item.cohortIds, activeCohorts, 'name'),
                    _effectiveDate: item.versionDate?.time,
                }));

            const newData = restructuredUpdatedCreatedBy(newSchedules);
            newSchedules = orderBy(newData, [{ field: '_effectiveDate', dir: 'asc' }]);
            setSchedules(newSchedules);
            setGroupedVersions(groupScheduleVersions(newSchedules));
        } else {
            setSchedules([]);
            setGroupedVersions([]);
        }
    };

    useEffect(() => {
        setStudy(getStudyData?.study ?? null);
    }, [getStudyData?.study]);

    useEffect(() => {
        if (data?.studyVisitSchedules) {
            restructureSchedules(data?.studyVisitSchedules);
        }
    }, [data?.studyVisitSchedules, activeCohorts, activeTreatmentArms]);

    useEffect(() => {
        if (dataCohorts?.cohorts) {
            setActiveCohorts(
                dataCohorts?.cohorts.filter(notEmpty).filter((cohort: StudyCohortFields) => cohort?.active),
            );
        }
    }, [dataCohorts]);

    useEffect(() => {
        if (dataTreatmentArms?.treatmentArms) {
            setActiveTreatmentArms(
                dataTreatmentArms?.treatmentArms
                    .filter(notEmpty)
                    .filter((treatmentArm: StudyTreatmentArmFields) => treatmentArm.active),
            );
        }
    }, [dataTreatmentArms]);

    useEffect(() => {
        if (currentSchedule && schedules) {
            const scheduleToUpdate = schedules.find((schedule: any) => schedule.id === currentSchedule.id);
            if (scheduleToUpdate) {
                setCurrentSchedule(scheduleToUpdate);
            }
        }
    }, [schedules]);

    const isAddVersion = scheduleDialogType === Constants.scheduleDialogTypes.addScheduleVersion;
    const isEditVersion = scheduleDialogType === Constants.scheduleDialogTypes.editScheduleVersion;

    if (!user || !user.systemAdminLevel) return null;

    if (!studyId || loading || loadingCohorts || loadingTreatmentArms || getStudyLoading) return <Loading />;

    if (error || errorCohorts || errorTreatmentArms) return <Error error={error} />;

    const canEditSubjectVisitTemplate = () => {
        const currentGroup = groupedVersions[Number(currentSchedule?.groupId)];
        if (currentGroup === undefined) return false;

        const latestVersion = currentGroup[currentGroup.length - 1];

        return canEdit && currentSchedule?.id === latestVersion.id;
    };

    return (
        <>
            {openDeleteVersions.value && (
                <DeleteScheduleVersionDialog
                    close={() => {
                        openDeleteVersions.setFalse();
                    }}
                    studyId={studyId}
                    currentSchedule={latestSchedule}
                    hasMultipleSchedules={hasMultipleVersions}
                />
            )}
            {openAddSchedules.value && (
                <AddVisitSchedule
                    currentSchedule={currentSchedule}
                    treatmentArms={activeTreatmentArms}
                    cohorts={activeCohorts}
                    schedules={
                        isAddVersion || isEditVersion ? groupedVersions[Number(currentSchedule?.groupId)] : schedules
                    }
                    allSchedules={schedules}
                    studyId={studyId}
                    scheduleDialogType={scheduleDialogType}
                    onClose={(action: string | boolean | undefined) => {
                        if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                        else {
                            if (action === 'add')
                                NotificationService.showNotification(t('admin:scheduleSuccessfulCreate'));
                            else if (action === 'edit') {
                                NotificationService.showNotification(
                                    t('admin:scheduleSuccessfulUpdate', { id: currentSchedule?.recordId }),
                                );
                            } else if (action === 'addVersion') {
                                NotificationService.showNotification(t('admin:scheduleVersionSuccessfulCreate'));
                            } else if (action === 'editVersion') {
                                NotificationService.showNotification(
                                    t('admin:scheduleVersionSuccessfulUpdate', { id: currentSchedule?.recordId }),
                                );
                            }
                            openAddSchedules.setFalse();
                            setCurrentSchedule(null);
                            setScheduleDialogType(null);
                        }
                    }}
                    isSysAdmin={isSysAdmin}
                />
            )}
            <ViewSubjectVisitTemplateDialog
                loadedData={data?.studyVisitSchedules}
                currentSchedule={currentSchedule}
                studyId={studyId}
                canEdit={canEditSubjectVisitTemplate()}
                open={showViewSVTDialog.value}
                onClose={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add' && currentSchedule)
                            NotificationService.showNotification(
                                t('', {
                                    id: currentSchedule?.recordId ?? currentSchedule?.id,
                                }),
                            );
                        setCurrentSchedule(null);
                        showViewSVTDialog.setFalse();
                    }
                }}
                assessmentAssociation={study?.assessmentAssociation}
            />
            {study && (
                <ImportAndTRSetupDialog
                    open={openImportAndTRSetupDialog.value}
                    onClose={openImportAndTRSetupDialog.setFalse}
                    study={study}
                    canEdit={canEdit}
                />
            )}
            <ScheduleBar
                groupedVersions={groupedVersions}
                openAddSchedules={() => {
                    openAddSchedules.setTrue();
                    setScheduleDialogType(Constants.scheduleDialogTypes.addScheduleGroup);
                    openAddSchedules.setTrue();
                }}
                openImportAndTRSetup={openImportAndTRSetupDialog.setTrue}
                study={study}
                canEdit={canEdit}
            />
            <div className="grid-list">
                {Object.keys(groupedVersions).map((group) => (
                    <div key={group} className="grid-list__item">
                        <ScheduleVersionsGrid
                            title={groupedVersions[Number(group)][0].name || ''}
                            canEdit={canEdit}
                            isSysAdmin={isSysAdmin}
                            schedules={groupedVersions[Number(group)]}
                            setCurrentSchedule={setCurrentSchedule}
                            openAddSchedules={(type: string | null) => {
                                setScheduleDialogType(type);
                                openAddSchedules.setTrue();
                            }}
                            openDeleteVersions={() => {
                                setLatestSchedule(groupedVersions[Number(group)].at(-1));
                                setHasMultipleVersions(groupedVersions[Number(group)]?.length > 1);
                                openDeleteVersions.setTrue();
                            }}
                            showViewSubjectVisitTemplateDialog={showViewSVTDialog.setTrue}
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

export default AdminStudyVisitSchedules;
